import { QueryClient, type QueryKey } from '@tanstack/react-query';

type OrvalFnNoParams<T> = (options?: RequestInit) => Promise<T>;

type OrvalFnWithParams<P extends object, T> = (
  params: P,
  options?: RequestInit
) => Promise<T>;

type EnsureApiQueryDataBase = {
  queryKey: QueryKey;
  staleTime?: number;
};

export type EnsureApiQueryDataNoParamsOptions<T> = EnsureApiQueryDataBase & {
  apiFn: OrvalFnNoParams<T>;
};

export type EnsureApiQueryDataOptions<
  P extends object,
  T,
> = EnsureApiQueryDataBase & {
  apiFn: OrvalFnWithParams<P, T>;
  params: P;
};

export type FetchApiQueryNoParamsOptions<T> = {
  queryKey: QueryKey;
  apiFn: OrvalFnNoParams<T>;
};

export type FetchApiQueryOptions<P extends object, T> = {
  queryKey: QueryKey;
  apiFn: OrvalFnWithParams<P, T>;
  params: P;
};

/**
 * Ensures cached data for a zero-arg Orval API function (e.g. route loaders).
 */
export function ensureApiQueryData<T>(
  queryClient: QueryClient,
  options: EnsureApiQueryDataNoParamsOptions<T>
): Promise<T>;

export function ensureApiQueryData<P extends object, T>(
  queryClient: QueryClient,
  options: EnsureApiQueryDataOptions<P, T>
): Promise<T>;

export function ensureApiQueryData<P extends object, T>(
  queryClient: QueryClient,
  options:
    | EnsureApiQueryDataNoParamsOptions<T>
    | EnsureApiQueryDataOptions<P, T>
): Promise<T> {
  const { queryKey, apiFn, staleTime } = options;

  if ('params' in options) {
    const { params } = options;
    return queryClient.ensureQueryData({
      queryKey,
      queryFn: ({ signal }) =>
        (apiFn as OrvalFnWithParams<P, T>)(params, { signal }),
      staleTime,
    });
  }

  return queryClient.ensureQueryData({
    queryKey,
    queryFn: ({ signal }) => (apiFn as OrvalFnNoParams<T>)({ signal }),
    staleTime,
  });
}

/**
 * Fetches and caches data for a zero-arg Orval API function.
 */
export function fetchApiQuery<T>(
  queryClient: QueryClient,
  options: FetchApiQueryNoParamsOptions<T>
): Promise<T>;

export function fetchApiQuery<P extends object, T>(
  queryClient: QueryClient,
  options: FetchApiQueryOptions<P, T>
): Promise<T>;

export function fetchApiQuery<P extends object, T>(
  queryClient: QueryClient,
  options: FetchApiQueryNoParamsOptions<T> | FetchApiQueryOptions<P, T>
): Promise<T> {
  const { queryKey, apiFn } = options;

  if ('params' in options) {
    const { params } = options;
    return queryClient.fetchQuery({
      queryKey,
      queryFn: ({ signal }) =>
        (apiFn as OrvalFnWithParams<P, T>)(params, { signal }),
    });
  }

  return queryClient.fetchQuery({
    queryKey,
    queryFn: ({ signal }) => (apiFn as OrvalFnNoParams<T>)({ signal }),
  });
}
